import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * PedagogieDrawer — modale latérale "Pourquoi ce jeu ?" (Sprint 1.8 L4).
 *
 * Charge dynamiquement `data/pedagogie` au premier render (chunk split — le
 * bundle initial des jeux n'est pas inflé) et rend les 4 sections via
 * <MarkdownRenderer>. Source canonique : `wiki/pedagogie/<slug>-pourquoi.md`.
 *
 * Accessibilité :
 *   - role="dialog" + aria-modal=true + aria-labelledby
 *   - Focus trap (focus revient sur le bouton fermer à l'ouverture)
 *   - ESC pour fermer + click outside (overlay)
 *   - Body scroll lock pendant ouverture
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MarkdownRenderer } from './MarkdownRenderer';
export function PedagogieDrawer({ gameSlug, isOpen, onClose }) {
    const { t } = useTranslation(['methode', 'common']);
    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(false);
    const closeBtnRef = useRef(null);
    const panelRef = useRef(null);
    // Lazy load data + body scroll lock + focus
    useEffect(() => {
        if (!isOpen)
            return undefined;
        setLoading(true);
        let cancelled = false;
        void (async () => {
            try {
                const mod = await import('../data/pedagogie');
                if (cancelled)
                    return;
                setEntry(mod.games[gameSlug] ?? null);
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        requestAnimationFrame(() => closeBtnRef.current?.focus());
        return () => {
            cancelled = true;
            document.body.style.overflow = prevOverflow;
        };
    }, [isOpen, gameSlug]);
    // ESC handler
    useEffect(() => {
        if (!isOpen)
            return undefined;
        const handler = (evt) => {
            if (evt.key === 'Escape')
                onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);
    if (!isOpen)
        return null;
    return (_jsxs("div", { role: "dialog", "aria-modal": "true", "aria-labelledby": `pedagogie-title-${gameSlug}`, className: "fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:justify-end", children: [_jsx("button", { type: "button", "aria-label": t('methode:drawer.close_aria'), onClick: onClose, className: "absolute inset-0 cursor-pointer bg-cyan-text/40 backdrop-blur-[2px]" }), _jsxs("div", { ref: panelRef, className: "\n          relative flex max-h-[88dvh] w-full flex-col gap-4 overflow-y-auto\n          border-t border-cyan-border bg-cyan-surface p-5\n          sm:max-h-[92dvh] sm:max-w-[430px] sm:border-t-0 sm:border-l\n          sm:p-6\n        ", children: [_jsxs("div", { className: "flex items-start justify-between gap-3", children: [_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-network", children: t('methode:drawer.title') }), _jsx("h2", { id: `pedagogie-title-${gameSlug}`, className: "font-display text-[20px] font-medium leading-7 tracking-[-0.02em] text-cyan-text", children: entry?.title ?? '…' })] }), _jsx("button", { ref: closeBtnRef, type: "button", onClick: onClose, "aria-label": t('methode:drawer.close_aria'), className: "\n              shrink-0 border border-cyan-border-faint px-3 py-1\n              text-[12px] text-cyan-text-secondary hover:border-cyan-network hover:text-cyan-text\n            ", children: t('common:actions.close') })] }), _jsx("p", { className: "text-[11px] text-cyan-text-secondary", children: t('methode:drawer.fr_only_notice') }), loading ? (_jsx("p", { className: "font-mono text-[12px] text-cyan-text-secondary", children: t('methode:drawer.loading') })) : entry ? (_jsx(MarkdownRenderer, { html: entry.html })) : (_jsx("p", { className: "text-[13px] text-cyan-text-secondary", children: t('methode:drawer.no_data') }))] })] }));
}
//# sourceMappingURL=PedagogieDrawer.js.map